import type { MessageKey } from "@wato/i18n";
import type { OverviewCharts as ChartsData } from "../../api/overview-types";
import { ChartCard } from "../../components/analytics/cards";
import { looksTechnical } from "../../lib/technical-text";
import { PEAK_DAY_KEYS, PeakHoursGrid } from "../results/PeakHoursGrid";
import { buildHeatGrid } from "./overview-model";
import { useOverviewFormat, type OverviewFormat } from "./useOverviewFormat";

/** Design-token colours for the channel series (first = brand colour). */
const SERIES_COLORS = [
  "var(--color-action-primary)",
  "var(--color-accent-fg)",
  "var(--color-info-icon)",
  "var(--color-warning-icon)",
  "var(--color-success-icon)",
  "var(--color-line-strong)",
] as const;

interface Series {
  readonly id: string;
  readonly label: string;
  readonly color: string;
}
interface DayBar {
  readonly date: string;
  /** Series id -> value; drawn bottom-up in series order. */
  readonly values: Readonly<Record<string, number>>;
  readonly total: number;
  /** Tooltip text. */
  readonly title: string;
}

/**
 * Bars per day (stacked when several series). The plot sits in an `overflow-x-auto` box with a
 * minimum width, so on a phone it scrolls sideways and never widens the page. The plot is an image for
 * assistive tech (`summary`); the exact numbers are in the visually hidden table.
 */
function DayBars({
  days,
  series,
  summary,
  tableHead,
  tableCells,
  f,
}: {
  readonly days: readonly DayBar[];
  readonly series: readonly Series[];
  readonly summary: string;
  readonly tableHead: readonly string[];
  readonly tableCells: (d: DayBar) => readonly string[];
  readonly f: OverviewFormat;
}) {
  const max = Math.max(1, ...days.map((d) => d.total));
  // Label every bar when there are few; otherwise about six labels across the plot.
  const step = Math.max(1, Math.ceil(days.length / 6));
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="overflow-x-auto" data-testid="overview-chart-scroll">
        <div role="img" aria-label={summary} className="min-w-[32rem]">
          <div className="flex h-40 items-end justify-evenly gap-1" dir="ltr">
            {days.map((d) => (
              <div
                key={d.date}
                title={d.title}
                className="flex h-full max-w-10 min-w-0 flex-1 flex-col justify-end"
              >
                <div
                  className="flex w-full flex-col-reverse overflow-hidden rounded-sm"
                  style={{ height: `${(d.total / max) * 100}%` }}
                >
                  {series.map((s) =>
                    (d.values[s.id] ?? 0) > 0 ? (
                      <span
                        key={s.id}
                        className="min-h-px"
                        style={{ flexGrow: d.values[s.id], backgroundColor: s.color }}
                      />
                    ) : null,
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-1 flex justify-evenly gap-1" dir="ltr" aria-hidden="true">
            {days.map((d, i) => (
              <span
                key={d.date}
                className="max-w-10 min-w-0 flex-1 text-center text-xs whitespace-nowrap text-fg-muted"
              >
                {i % step === 0 ? f.day(d.date) : ""}
              </span>
            ))}
          </div>
        </div>
      </div>
      {series.length > 1 ? (
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-fg-muted">
          {series.map((s) => (
            <li key={s.id} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="size-2.5 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </li>
          ))}
        </ul>
      ) : null}
      <table className="sr-only">
        <caption>{summary}</caption>
        <thead>
          <tr>
            {tableHead.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date}>
              <th scope="row">{f.day(d.date)}</th>
              {tableCells(d).map((cell, i) => (
                <td key={i}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const maxOf = <T,>(xs: readonly T[], by: (x: T) => number): T | undefined =>
  xs.reduce<T | undefined>(
    (best, x) => (best === undefined || by(x) > by(best) ? x : best),
    undefined,
  );

function ConversationsChart({
  charts,
  f,
}: {
  readonly charts: ChartsData;
  readonly f: OverviewFormat;
}) {
  const { t } = f;
  const perDay = charts.conversationsPerDay ?? [];
  const kindOther = t("dashboard.aiAgents.channel.other");
  const known = (charts.conversationChannels ?? []).map((c) => ({
    id: c.id,
    label: c.name && !looksTechnical(c.name) ? c.name : kindOther,
  }));
  // Channels that show up in a day but not in the channel list still get a series of their own.
  const extra = [...new Set(perDay.flatMap((d) => Object.keys(d.byChannel)))].filter(
    (id) => !known.some((c) => c.id === id),
  );
  const series: Series[] = [...known, ...extra.map((id) => ({ id, label: kindOther }))].map(
    (c, i) => ({ ...c, color: SERIES_COLORS[i % SERIES_COLORS.length]! }),
  );
  const days: DayBar[] = perDay.map((d) => {
    const total = Object.values(d.byChannel).reduce((a, b) => a + b, 0);
    return { date: d.date, values: d.byChannel, total, title: `${f.day(d.date)}: ${f.num(total)}` };
  });
  const total = days.reduce((a, d) => a + d.total, 0);
  const best = maxOf(days, (d) => d.total);
  const summary = t("dashboard.overview.chart.conversationsSummary", {
    total,
    days: days.length,
    date: best ? f.day(best.date) : "",
    max: f.num(best?.total ?? 0),
  });
  return (
    <ChartCard title={t("dashboard.overview.section.conversationsChart")}>
      <DayBars
        days={days}
        series={series}
        summary={summary}
        tableHead={[
          t("dashboard.overview.chart.day"),
          ...series.map((s) => s.label),
          t("dashboard.overview.chart.total"),
        ]}
        tableCells={(d) => [...series.map((s) => f.num(d.values[s.id] ?? 0)), f.num(d.total)]}
        f={f}
      />
    </ChartCard>
  );
}

function SalesChart({
  charts,
  currency,
  f,
}: {
  readonly charts: ChartsData;
  readonly currency: string;
  readonly f: OverviewFormat;
}) {
  const { t } = f;
  const perDay = charts.salesPerDay ?? [];
  const orders = new Map(perDay.map((d) => [d.date, d.orders]));
  const days: DayBar[] = perDay.map((d) => ({
    date: d.date,
    values: { sales: Number(d.totalMinor) },
    total: Number(d.totalMinor),
    title: `${f.day(d.date)}: ${f.money(d.totalMinor, currency)}`,
  }));
  const money = new Map(perDay.map((d) => [d.date, d.totalMinor]));
  const sumMinor = perDay.reduce((a, d) => a + BigInt(d.totalMinor), 0n).toString();
  const best = maxOf(perDay, (d) => Number(d.totalMinor));
  const summary = t("dashboard.overview.chart.salesSummary", {
    total: f.money(sumMinor, currency),
    days: perDay.length,
    date: best ? f.day(best.date) : "",
    max: best ? f.money(best.totalMinor, currency) : "",
  });
  return (
    <ChartCard title={t("dashboard.overview.section.salesChart")}>
      <DayBars
        days={days}
        series={[
          { id: "sales", label: t("dashboard.overview.chart.sales"), color: SERIES_COLORS[0] },
        ]}
        summary={summary}
        tableHead={[
          t("dashboard.overview.chart.day"),
          t("dashboard.overview.chart.sales"),
          t("dashboard.overview.chart.orders"),
        ]}
        tableCells={(d) => [
          f.money(money.get(d.date) ?? "0", currency),
          f.num(orders.get(d.date) ?? 0),
        ]}
        f={f}
      />
    </ChartCard>
  );
}

function BusyHoursChart({
  charts,
  f,
}: {
  readonly charts: ChartsData;
  readonly f: OverviewFormat;
}) {
  const { t } = f;
  const { grid, max, peak } = buildHeatGrid(charts.busiestHours ?? []);
  const summary = peak
    ? t("dashboard.overview.chart.busySummary", {
        day: t(`peakHours.day.${PEAK_DAY_KEYS[peak.weekday]}` as MessageKey),
        hour: peak.hour,
        count: f.num(peak.count),
      })
    : t("dashboard.overview.section.busyHours");
  return (
    <ChartCard title={t("dashboard.overview.section.busyHours")}>
      <p className="m-0 text-xs text-fg-muted">{t("dashboard.overview.chart.busyHint")}</p>
      <div className="overflow-x-auto" data-testid="overview-chart-scroll">
        <PeakHoursGrid grid={grid} max={max} caption={summary} />
      </div>
    </ChartCard>
  );
}

/** OWN-164 charts: conversations per day (stacked by channel), sales per day, busiest hours. */
export function OverviewCharts({
  charts,
  currency,
  show,
}: {
  readonly charts: ChartsData;
  readonly currency: string;
  readonly show: {
    readonly conversationsChart: boolean;
    readonly salesChart: boolean;
    readonly busyHours: boolean;
  };
}) {
  const f = useOverviewFormat();
  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
      {show.conversationsChart ? (
        <div className="min-w-0" data-testid="overview-chart-conversations">
          <ConversationsChart charts={charts} f={f} />
        </div>
      ) : null}
      {show.salesChart ? (
        <div className="min-w-0" data-testid="overview-chart-sales">
          <SalesChart charts={charts} currency={currency} f={f} />
        </div>
      ) : null}
      {show.busyHours ? (
        <div className="min-w-0 lg:col-span-2" data-testid="overview-chart-busy">
          <BusyHoursChart charts={charts} f={f} />
        </div>
      ) : null}
    </div>
  );
}
